import { useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './ErrorRecoveryDemoAgentic.module.css';

type Dot = 'done' | 'active' | 'error';
type Stage = 'paused' | 'reconnecting' | 'working' | 'complete';
type Outcome = 'database' | 'public' | 'skipped';

interface LogEntry {
  time: string;
  dot: Dot;
  text: string;
}

const DOT_CLASS: Record<Dot, string> = {
  done: kit.logDotDone,
  active: kit.logDotActive,
  error: kit.logDotError,
};

const INITIAL_LOG: LogEntry[] = [
  { time: '12:58', dot: 'done', text: 'Collected 41 sources for the brief' },
  { time: '13:05', dot: 'done', text: "Drafted sections 1-6 of 'Home Battery Market 2026'" },
  { time: '13:06', dot: 'error', text: 'Stopped at section 7: access to the Voltdata market database was revoked' },
];

export function ErrorRecoveryDemoAgentic() {
  const [log, setLog] = useState<LogEntry[]>(INITIAL_LOG);
  const [stage, setStage] = useState<Stage>('paused');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [retried, setRetried] = useState(false);
  const [feedback, setFeedback] = useState(
    'The run hit a persistent error. Six sections are safe - choose how to handle the last two.'
  );
  const timer = useRef<number>();
  const minute = useRef(6);
  const stamp = (step = 1) => {
    minute.current += step;
    return `13:${String(minute.current).padStart(2, '0')}`;
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const add = (entry: LogEntry) => setLog((l) => [...l, entry]);
  const replaceLast = (entry: LogEntry) => setLog((l) => [...l.slice(0, -1), entry]);

  const retry = () => {
    setRetried(true);
    add({ time: stamp(), dot: 'error', text: 'Retry failed - access is still revoked. Sections 1-6 untouched.' });
    setFeedback("Same error, same step. It's persistent, so retrying alone won't help - try another option.");
  };

  const reconnect = () => {
    setStage('reconnecting');
    add({ time: stamp(), dot: 'active', text: 'Waiting for you to reconnect the Voltdata account' });
    setFeedback('The run stays paused with your progress intact until you finish signing in.');
  };

  const finishSignIn = () => {
    setStage('working');
    setOutcome('database');
    replaceLast({ time: stamp(), dot: 'done', text: 'Voltdata reconnected as Maya Ortiz' });
    add({ time: stamp(), dot: 'active', text: 'Drafting sections 7-8 from the market database' });
    setFeedback('Resuming from section 7, not from the start.');
    timer.current = window.setTimeout(() => {
      replaceLast({ time: stamp(3), dot: 'done', text: 'Sections 7-8 drafted from the market database' });
      setStage('complete');
      setFeedback('Report complete with full-confidence data. No finished section was redone.');
    }, 1800);
  };

  const usePublic = () => {
    setStage('working');
    setOutcome('public');
    add({ time: stamp(), dot: 'active', text: 'Drafting sections 7-8 from public filings' });
    setFeedback('Resuming from section 7 with a different source.');
    timer.current = window.setTimeout(() => {
      replaceLast({
        time: stamp(5),
        dot: 'done',
        text: "Sections 7-8 drafted from public filings - marked 'lower confidence'",
      });
      setStage('complete');
      setFeedback('The run finished on an alternative path, and the weaker sources are labeled, not hidden.');
    }, 1800);
  };

  const skip = () => {
    setStage('complete');
    setOutcome('skipped');
    add({ time: stamp(), dot: 'done', text: 'Report finalized with sections 1-6; 7-8 listed as open gaps' });
    setFeedback('You get the finished work now, with the gap stated plainly in the report.');
  };

  const sectionChip = (n: number) => {
    if (n <= 6) return <span key={n} className={`${kit.chip} ${kit.chipGood}`}>{n} Saved</span>;
    if (stage === 'working') return <span key={n} className={kit.chip}>{n} Drafting...</span>;
    if (outcome === 'database' && stage === 'complete')
      return <span key={n} className={`${kit.chip} ${kit.chipGood}`}>{n} Done</span>;
    if (outcome === 'public')
      return <span key={n} className={`${kit.chip} ${kit.chipWarn}`}>{n} Lower confidence</span>;
    if (outcome === 'skipped') return <span key={n} className={kit.chip}>{n} Skipped</span>;
    return <span key={n} className={`${kit.chip} ${kit.chipBad}`}>{n} Blocked</span>;
  };

  const title =
    stage === 'complete'
      ? outcome === 'skipped'
        ? 'Report delivered - 6 of 8 sections'
        : 'Report complete - 8 of 8 sections'
      : 'Run paused - 6 of 8 sections saved';

  return (
    <div className={kit.root}>
      <div className={kit.message}>Write a market report on home batteries for 2026.</div>

      <div className={kit.log}>
        {log.map((entry, i) => (
          <div key={i} className={kit.logRow}>
            <span className={kit.logTime}>{entry.time}</span>
            <span className={`${kit.logDot} ${DOT_CLASS[entry.dot]}`} aria-hidden="true" />
            <span>{entry.text}</span>
            <span />
          </div>
        ))}
      </div>

      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>{stage === 'complete' ? 'Run finished' : 'Needs your decision'}</span>
          <span>Home Battery Market 2026</span>
        </div>
        <h3 className={kit.proposalTitle}>{title}</h3>
        <div className={styles.sections}>{[1, 2, 3, 4, 5, 6, 7, 8].map(sectionChip)}</div>

        {stage === 'paused' && (
          <>
            <p className={styles.note}>
              Sections 7 and 8 need pricing data from Voltdata, and your access has ended.
              Retrying won't fix this on its own.
            </p>
            <div className={`${kit.row} ${styles.actions}`}>
              <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={reconnect}>
                Reconnect account
              </button>
              <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={usePublic}>
                Use public sources
              </button>
              <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={skip}>
                Skip sections 7-8
              </button>
              <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={retry} disabled={retried}>
                Retry anyway
              </button>
            </div>
          </>
        )}

        {stage === 'reconnecting' && (
          <div className={`${kit.row} ${styles.actions}`}>
            <button type="button" className={`${kit.btn} ${kit.btnApprove}`} onClick={finishSignIn}>
              Sign in to Voltdata
            </button>
            <button
              type="button"
              className={`${kit.btn} ${kit.btnGhost}`}
              onClick={() => {
                setStage('paused');
                replaceLast({ time: stamp(), dot: 'done', text: 'Reconnect canceled - run still paused at section 7' });
                setFeedback('Back to the options. Nothing was lost by changing your mind.');
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <p className={kit.feedback} aria-live="polite">{feedback}</p>
    </div>
  );
}
